import { PHONE_MSG, type LineMemoList, type LinePhotoList } from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { api, messageOf } from "../api.js";
import { ErrorText } from "./ui.jsx";
import { t } from "../labels.js";

/**
 * PC の見積の画面の、行の写真・社内メモの印（#117、docs/44 §4「PC 側」）。名称の右に小さく「写真 n」「メモ n」。
 * 押すと一覧の窓（写真は押すと大きく＝新しいタブ、メモは新しい順・読むだけ）。印は写真かメモがあるときだけ描く（無い見積の HTML は今と同じ）
 */
export function LineAttMark({ photos, memos, onOpen }: { photos: number; memos: number; onOpen: () => void }) {
  if (!photos && !memos) return null;
  const label = [photos ? `写真 ${photos}枚` : "", memos ? `メモ ${memos}件` : ""].filter(Boolean).join("・");
  return (
    <button
      type="button"
      className="lnatt"
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      data-testid="line-att"
    >
      {photos ? (
        <>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3z" />
            <circle cx="12" cy="13" r="3.5" />
          </svg>
          <span data-testid="line-att-photos">{photos}</span>
        </>
      ) : null}
      {memos ? (
        <>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <span data-testid="line-att-memos">{memos}</span>
        </>
      ) : null}
    </button>
  );
}

export function LineAttachmentsDialog({ estimateId, lineId, lineName, onClose, photos: withPhotos = true }: { estimateId: string; lineId: string; lineName: string; onClose: () => void; /** 写真も出すか（モジュール field の会社だけ。#142 段2）。無い会社はメモだけ */ photos?: boolean }) {
  const photos = useQuery({ queryKey: ["estimate-photos", estimateId], queryFn: () => api<LinePhotoList>("GET", `/estimates/${estimateId}/photos`), enabled: withPhotos });
  const memos = useQuery({ queryKey: ["line-memos", estimateId, lineId], queryFn: () => api<LineMemoList>("GET", `/estimates/${estimateId}/lines/${lineId}/memos`) });
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose]);
  const mine = (photos.data?.items ?? []).filter((p) => p.lineIds.includes(lineId));
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dlg attdlg" role="dialog" aria-modal="true" aria-label={`${lineName || "行"}の写真とメモ`} data-testid="line-att-dialog">
        <div className="dh">{lineName || t("（{lineName}なし）")}</div>
        <div className="db">
          {photos.isError ? <ErrorText>{messageOf(photos.error)}</ErrorText> : null}
          {mine.length ? (
            <div className="attgrid">
              {mine.map((p) => (
                <a key={p.id} href={p.url} target="_blank" rel="noreferrer" title={p.caption || "写真を大きく"}>
                  <img src={p.thumbUrl} alt={p.caption || "写真"} />
                </a>
              ))}
            </div>
          ) : null}
          {memos.data?.items.length ? (
            <>
              <b className="muted">{PHONE_MSG.memoHeading}</b>
              <ul className="attmemos">
                {memos.data.items.map((m) => (
                  <li key={m.id} data-testid="line-att-memo">
                    <span className="who">
                      {new Date(m.createdAt).toLocaleString("ja-JP", { timeZone: "Asia/Tokyo", month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" })} {m.createdByName}
                    </span>
                    <span>{m.body}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
        <div className="df">
          <button type="button" className="btn" onClick={onClose} autoFocus>
            {PHONE_MSG.close}
          </button>
        </div>
      </div>
    </div>
  );
}
